import type { StudySelection } from './marketplaceApi';

/**
 * Session-scoped draft of the study wizard.
 *
 * The wizard carried ALL of its state in React Router's `location.state`, and the
 * in-progress form content lived only in a component ref that was never persisted
 * until Next was pressed. So a hard reload (or a direct URL) on any step redirected
 * to step 1 and silently discarded the use case, the audience AND the entire typed
 * survey — no warning, no recovery (#73).
 *
 * sessionStorage (not localStorage) on purpose: a draft is scoped to this tab and
 * this sitting. It must not resurrect a half-finished study in a new tab days later.
 */
export interface WizardDraft {
  useCase?: string;
  selections?: StudySelection[];
  twinCount?: number;
  paid?: boolean;
  /** The draft study id, once created — so a restored wizard reuses it instead of
   *  creating a duplicate draft on every reload. */
  studyId?: number;
  /** A pre-payment study this wizard pass has abandoned (the user resumed it and then
   *  changed the use case or audience, so its row cannot be reused). Sent to the server
   *  on create so it is retired and its Stripe checkout expired, instead of lingering
   *  in history as a ghost "Awaiting payment" entry that is still payable. */
  supersededStudyId?: number;
  /** The use-case form's editable state (question text, concept fields, ...). */
  savedFormData?: Record<string, unknown>;
  updatedAt: number;
}

const KEY = 'mkt.wizard.draft.v1';

/**
 * Marketplace user the draft belongs to. The draft carries the study's `studyId`
 * and typed inputs, so on a shared machine an un-namespaced key let user B (same
 * tab, after A logged out) read A's draft AND reuse A's studyId — patching another
 * user's study. Scoping the key by user id makes B's key distinct from A's, so one
 * account can never read another's draft. Set from MarketplaceAuthProvider on the
 * active user; null (logged out) falls back to the base key.
 */
let scopeId: string | number | null = null;

export function setDraftScope(id: string | number | null): void {
  scopeId = id;
}

function scopedKey(): string {
  return scopeId != null ? `${KEY}.${scopeId}` : KEY;
}

/** Stale drafts are worse than none — they silently resurrect an abandoned study. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12h

export function saveDraft(patch: Omit<Partial<WizardDraft>, 'updatedAt'>): void {
  try {
    const current = loadDraft() || {};
    const next: WizardDraft = { ...current, ...patch, updatedAt: Date.now() };
    sessionStorage.setItem(scopedKey(), JSON.stringify(next));
  } catch {
    /* private mode / quota — the wizard still works, it just won't survive a reload */
  }
}

export function loadDraft(): WizardDraft | null {
  try {
    const raw = sessionStorage.getItem(scopedKey());
    if (!raw) return null;
    const draft = JSON.parse(raw) as WizardDraft;
    if (!draft || typeof draft !== 'object') return null;
    if (Date.now() - (draft.updatedAt || 0) > MAX_AGE_MS) {
      clearDraft();
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(scopedKey());
  } catch {
    /* nothing to do */
  }
}

/** True when the draft carries enough to rebuild a wizard step (use case + audience). */
export function draftIsUsable(draft: WizardDraft | null): draft is WizardDraft {
  return !!draft?.useCase && (draft.selections?.length ?? 0) > 0;
}

/**
 * The use case for a picker screen, resolved from the most durable source first.
 *
 * The wizard step lives in the URL (`?step=audience`) so the browser Back/Forward buttons move
 * between the picker's two screens. But `setSearchParams` pushes those history entries with NO
 * router state, so a browser Back from the fill step landed on `?step=audience` with an empty
 * `location.state`, the picker rebuilt `useCase` as null, and its "preselect the first card"
 * fallback silently turned it into the first use case — survey — which then rendered the survey
 * form (no image upload) for what should have been packaging, and overwrote the draft on continue.
 *
 * Fixing it means never reconstructing `useCase` from `location.state` alone. Priority:
 *   1. the URL query (`?useCase=`) — the one channel browser Back/Forward preserves verbatim,
 *   2. router `location.state` — set by every forward navigation,
 *   3. the session draft — survives a hard reload.
 */
export function resolveWizardUseCase(
  urlUseCase: string | null,
  stateUseCase: string | undefined,
  isKnownUseCase?: (uc: string) => boolean,
  // The draft's use case, supplied by the caller. Taken as a PARAMETER (not read via an
  // internal loadDraft) so the caller controls WHICH draft applies — after the #274 rebase
  // that means the user-scoped, paid/free-gated draft, not a bare cross-flow loadDraft().
  draftUseCase?: string,
): string | null {
  const resolved = urlUseCase || stateUseCase || draftUseCase || null;
  // An UNKNOWN key (a hand-edited or stale `?useCase=bogus`) is worse than none: the
  // picker's screen-2 guard only checks `!useCase`, so a truthy-but-unknown value is
  // truthy while its meta is undefined → the screen renders nothing (no content, no
  // Continue, no recovery). Treat unknown as null so it falls back to the neutral
  // picker. Only validates when a predicate is supplied (kept optional so pure-logic
  // callers/tests need not wire up the UI meta).
  if (resolved && isKnownUseCase && !isKnownUseCase(resolved)) return null;
  return resolved;
}
